import { upperFirst } from "lodash";
import type { OtherFieldGroupFields } from "@/entities/form-metadata";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";

/**
 * UBE focus tracking for `OtherFieldsGroup`'s dynamic fields (Keyword, Self Gender, Self Race,
 * …). Unlike the top-level Name fields, these aren't drawn from a fixed, enumerable list, so
 * identity is compositional (`relation`/`eventName`/`fieldName`) rather than a constant
 * `objectId` — see `docs/engineering/ube.md`.
 *
 * `relation` and `eventType` come from the field-group metadata as plain (possibly empty)
 * strings; an empty string means "no relation/event concept applies" and is omitted from the
 * payload entirely rather than sent as `""`. `assertion`'s casing is inconsistent in the raw
 * metadata (e.g. `"keyword"` vs. `"Gender"`), so it's normalized to match the contract's
 * PascalCase `fieldName` values (`Keyword`, `Gender`, `Race`, …).
 *
 * Returns a lookup function rather than tracking a single item directly, so it can be called
 * once per item inside a render loop without violating the Rules of Hooks.
 */
export function useOtherFieldFocusTracking(): (
    item: Pick<OtherFieldGroupFields, "relation" | "eventType" | "assertion">,
) => { onFocus: () => void } {
    const track = useTrackSearchFormInteraction();

    return ({ relation, eventType, assertion }) => ({
        onFocus: () =>
            track(
                {
                    action: "focus",
                    ...(relation !== "" && { relation }),
                    ...(eventType !== "" && { eventName: eventType }),
                    fieldName: upperFirst(assertion),
                },
                { dedup: true },
            ),
    });
}
